import type { SectionRendererProps } from '../section-renderer.types';

interface StatItem {
  label: string;
  value: string;
}

interface StatsBarContent {
  eyebrow?: string;
  stats: StatItem[];
}

/**
 * How a page lays its stats band out (FR-307).
 *
 * `inline` puts the eyebrow in its own left-aligned column beside the stats,
 * divided by a vertical rule; `stacked` centres it above a centred stat grid.
 */
type StatsBarLayout = 'inline' | 'stacked';

interface StatsBarStyle {
  layout: StatsBarLayout;
  /** Band background. */
  background: string;
  /** Eyebrow colour. The stats themselves are white on every band. */
  eyebrow: string;
}

/**
 * Stats band (FR-109, FR-120, FR-132, FR-142; FR-307).
 *
 * One entry per page rather than a chain of `pageSlug === ...` tests. Before
 * MYN-326 this file branched on `pageSlug` three separate times - once for the
 * layout and twice more as colour ternaries - which mnr-standards bans
 * ("no repeated ternary/if-chains on the same discriminant"): the case lists
 * had already drifted, and FR-307 would have added a fourth. The page is
 * resolved once here and every field read off that single result.
 *
 * Benefits was the client's reference for the inline layout; MYN-326 moved
 * Home and Fundraising Support onto it. Investors and Trusted Partners stay
 * centred (A-343), and the alignment change deliberately carries no colour
 * change with it (A-340) - each band keeps the palette its own page shipped.
 */
const STATS_BAR_STYLE: Record<string, StatsBarStyle> = {
  home: { layout: 'inline', background: 'bg-navy', eyebrow: 'text-dkgreen' },
  benefits: { layout: 'inline', background: 'bg-dkgreen', eyebrow: 'text-navy' },
  'due-diligence-platform': { layout: 'inline', background: 'bg-navy', eyebrow: 'text-dkgreen' },
  // Teal band with the lighter brand green, confirmed from Figma's own
  // get_design_context data - not the navy/dkgreen pairing every other page uses.
  investors: { layout: 'stacked', background: 'bg-teal', eyebrow: 'text-green' },
  'trusted-partners': { layout: 'stacked', background: 'bg-navy', eyebrow: 'text-dkgreen' },
};

/** Any page without its own entry renders the original centred treatment. */
const DEFAULT_STATS_BAR_STYLE: StatsBarStyle = {
  layout: 'stacked',
  background: 'bg-navy',
  eyebrow: 'text-dkgreen',
};

/**
 * One stat. `className` carries the layout-specific part: the inline band
 * shares the row width between its cells (`flex-1`), the stacked one sits each
 * cell in a grid track that already has a width.
 */
function StatCell({ stat, className }: { stat: StatItem; className: string }) {
  return (
    <div className={`flex flex-col items-center gap-2 text-center sm:px-8 ${className}`}>
      <p className="text-h1 text-white">{stat.value}</p>
      <p className="text-p4 text-white">{stat.label}</p>
    </div>
  );
}

export function StatsBarRenderer({ section, pageSlug }: SectionRendererProps) {
  const c = section.content as unknown as StatsBarContent;
  const style = STATS_BAR_STYLE[pageSlug] ?? DEFAULT_STATS_BAR_STYLE;

  if (style.layout === 'inline') {
    return (
      <section className={`w-full ${style.background}`}>
        <div className="mx-auto flex w-full max-w-328 flex-col items-stretch gap-8 px-4 py-12 sm:flex-row sm:divide-x sm:divide-white/20 sm:py-16">
          {c.eyebrow && (
            <p className={`text-p4 max-w-56 font-bold uppercase sm:flex sm:items-center sm:pr-8 ${style.eyebrow}`}>
              {c.eyebrow}
            </p>
          )}
          {c.stats?.map((stat, i) => (
            <StatCell key={i} stat={stat} className="flex-1 justify-center" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className={`w-full ${style.background}`}>
      <div className="mx-auto flex w-full max-w-328 flex-col gap-8 px-4 py-16">
        {c.eyebrow && (
          <p className={`text-p6 text-center font-semibold uppercase ${style.eyebrow}`}>{c.eyebrow}</p>
        )}
        {/* sm:divide-x adds the thin separator line between stats the client's
          design review showed. */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:divide-x sm:divide-white/20">
          {c.stats?.map((stat, i) => (
            <StatCell key={i} stat={stat} className="" />
          ))}
        </div>
      </div>
    </section>
  );
}
